import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CardBody, Modal } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import CardHeader from "../../Common/CardHeader/CardHeader";
import search from "../../assets/images/search-lg.svg";
import { getInvoiceList } from "../../../redux/invoice/action";
import InvoiceFilter from "./InvoiceFilter";
import Check from "../../assets/images/checkk.svg";
import Cross from "../../assets/images/cross.svg";
import CommonPagination from "../../Common/Pagination/CommonPagination";
import FullScreenLoader from "../../Layouts/FullScreenLoader/FullScreenLoader";
import ZatcaModal from "./ZatcaModal";
import { getZatcaStatusDisplay } from "../../../utils/zatcaStatus";

const DOCUMENT_TYPE_LABELS = {
  0: "Simplified",
  1: "TAX",
  2: "Purchase",
  3: "Credit Note",
  4: "Debit Note",
};

const getRequestedDocumentType = (searchParams) => {
  const requestedType = new URLSearchParams(searchParams).get("invoice_type");
  return ["0", "1", "2", "3", "4"].includes(requestedType)
    ? requestedType
    : "";
};

const getInitialParams = (searchParams) => {
  return {
    search_key: "",
    page: 1,
    user_id: "",
    start_date: "",
    end_date: "",
    status: "",
    invoice_type: getRequestedDocumentType(searchParams),
    environment: "",
    testing_account_filter: "hide",
    sort_by: "date_time",
    sort_order: "desc",
  };
};

const displayValue = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || "N/A";
};

const displayMobile = (countryCode, mobileNumber) => {
  const mobile = String(mobileNumber ?? "").trim();
  if (!mobile) return "N/A";
  return `${String(countryCode ?? "").trim()} ${mobile}`.trim();
};

const InvoiceList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [modals, setModals] = useState(false);
  const [zatcaMod, setZatcaMod] = useState(false);
  const [zatDetails, setZatDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [params, setParams] = useState(() => getInitialParams(location.search));
  const invoiceList = useSelector((state) => state?.reusableSlice?.invoiceList);

  useEffect(() => {
    const requestedType = getRequestedDocumentType(location.search);
    setParams((currentParams) =>
      currentParams.invoice_type === requestedType
        ? currentParams
        : {
            ...currentParams,
            invoice_type: requestedType,
            page: 1,
          }
    );
  }, [location.search]);

  useEffect(() => {
    // Keep the header mounted during filter/search refreshes. It synchronizes
    // search parameters on mount, so remounting it here would trigger a loop.
    dispatch(getInvoiceList(params))
      .finally(() => setIsLoading(false));
  }, [dispatch, params]);

  const handleZatcaDetails = (document) => {
    setZatDetails(document);
    setZatcaMod(true);
  };

  return (
    <div className="py-3">
      {isLoading ? (
        <FullScreenLoader />
      ) : (
        <div>
          <InvoiceFilter
            modal={modals}
            setModal={setModals}
            params={params}
            setParams={setParams}
          />
          <CardHeader
            params={params}
            headerTitle="Customer Documents"
            setParams={setParams}
            addmodel={() => navigate("/addinvoice")}
            search={search}
            modals={modals}
            setModals={setModals}
            searchPlaceholder="Search by user ID, company, document number, customer, supplier, email, or mobile"
          />

          <CardBody className="table-sec px-3">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">User ID</th>
                  <th scope="col">Company</th>
                  <th scope="col">Document Number</th>
                  <th scope="col">Environment</th>
                  <th scope="col">Customer / Supplier</th>
                  <th scope="col">Email ID</th>
                  <th scope="col">Mobile Number</th>
                  <th scope="col">Document Type</th>
                  <th scope="col">Date</th>
                  <th scope="col">Document Total (SAR)</th>
                  <th scope="col">ZATCA Integration</th>
                  <th scope="col">ZATCA Status</th>
                  <th scope="col">Signed</th>
                  <th scope="col" className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {invoiceList?.invoices?.length > 0 ? (
                  invoiceList.invoices.map((item) => {
                    const zatcaDisplay = getZatcaStatusDisplay(item);
                    const isNote = [3, 4].includes(Number(item?.invoice_type));

                    return (
                      <tr key={item?.id}>
                        <td>{displayValue(item?.user_id)}</td>
                        <td>{displayValue(item?.company_name)}</td>
                        <td>
                          <div>{displayValue(item?.invoice_number)}</div>
                          {isNote && item?.parent_invoice_number ? (
                            <div className="admin-inline-note">
                              Parent: {item.parent_invoice_number}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <span className={`sf-env-badge ${item?.is_test ? "sandbox" : "live"}`}>
                            {item?.is_test ? "TEST" : "LIVE"}
                          </span>
                        </td>
                        <td>{displayValue(item?.counterparty_name)}</td>
                        <td>{displayValue(item?.counterparty_email)}</td>
                        <td>
                          {displayMobile(
                            item?.counterparty_country_code,
                            item?.counterparty_mobile_number
                          )}
                        </td>
                        <td>
                          {DOCUMENT_TYPE_LABELS[Number(item?.invoice_type)] || "N/A"}
                        </td>
                        <td>{displayValue(item?.date_time)}</td>
                        <td>{displayValue(item?.total)}</td>
                        <td>{item?.onboarding === 1 ? "Phase 2" : "Phase 1"}</td>
                        <td>
                          <span
                            style={{
                              color:
                                zatcaDisplay.success === true
                                  ? "#198754"
                                  : zatcaDisplay.success === false
                                  ? "#b42318"
                                  : "#6b7280",
                              fontWeight: 600,
                            }}
                          >
                            {zatcaDisplay.label}
                          </span>
                        </td>
                        <td>
                          {item?.onboarding !== 1 ||
                          String(item?.zatca_status || "").toUpperCase() ===
                            "INTERNAL_ONLY" ? (
                            <h5>--</h5>
                          ) : (
                            <img
                              src={item?.signed_xml_url ? Check : Cross}
                              alt={item?.signed_xml_url ? "Signed" : "Not signed"}
                            />
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center justify-content-center">
                            <button
                              className="border-button"
                              onClick={() => handleZatcaDetails(item)}
                            >
                              <span>ZATCA</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="14" className="text-center">
                      No Customer Documents Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardBody>
          <CommonPagination
            params={params}
            setParams={setParams}
            pagination_meta={invoiceList?.pagination_meta}
          />
          <Modal
            size="lg"
            isOpen={zatcaMod}
            className="zatca-modal"
            toggle={() => setZatcaMod((state) => !state)}
          >
            <ZatcaModal
              zatcaMod={zatcaMod}
              setZatcaMod={setZatcaMod}
              data={zatDetails}
            />
          </Modal>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;
